import type { Figures, InvoiceAggregateDTO, InvoiceTotals } from "./reporting";

/**
 * Reading the sales figures — the one place the browser decides what a number
 * means.
 *
 * Before this, four money definitions and three unit definitions were live
 * across ten screens, none of them named, and the dashboard disagreed with the
 * analysis page by $2,917 and four units for September. The arithmetic now
 * happens once, on the server, and arrives named; this module only picks.
 */

/** Which deductions apply. */
export type Basis = "billed" | "net";

/** Which invoices count. */
export type Scope = "all" | "issued";

/**
 * Which unit count: what was sold (a kit is one), or the SKUs that shipped
 * (a kit's contents, one each). See `Figures.items_billed` for what the pair
 * means under a part grouping, where a screen labels them Total and Direct.
 */
export type Count = "products" | "items";

export const ZERO: Figures = {
  billed: 0,
  discounts: 0,
  returns: 0,
  net: 0,
  units_billed: 0,
  items_billed: 0,
};

/**
 * The figures for one scope.
 *
 * `all` is on every aggregate. `issued` is null when the query behind the
 * aggregate was not grouped by status — see `FiguresByScope` — and in that
 * case this answers with `all`, which is the closest true figure. Callers that
 * need to know the difference ask `issuedAvailable` first; today the only
 * screens that read the issued scope use presets that always carry it.
 */
export function figuresFor(
  aggregate: InvoiceAggregateDTO | null | undefined,
  scope: Scope,
): Figures {
  const sent = aggregate?.figures;
  if (!sent) return ZERO;
  return scope === "issued" && sent.issued ? sent.issued : sent.all;
}

/** Whether this aggregate's query could answer the issued scope at all. */
export function issuedAvailable(aggregate: InvoiceAggregateDTO | null | undefined): boolean {
  return Boolean(aggregate?.figures?.issued);
}

/** Billed money less nothing, or less discounts and returns. */
export function money(figures: Figures, basis: Basis): number {
  return basis === "net" ? figures.net : figures.billed;
}

/**
 * Units on the chosen count. The basis is accepted so every caller reads
 * money and units the same way, and ignored: units have no net (see
 * `Figures`).
 */
export function units(figures: Figures, _basis: Basis, count: Count = "products"): number {
  return count === "items" ? figures.items_billed : figures.units_billed;
}

/** Shorthand for the common pair. */
export function figure(
  aggregate: InvoiceAggregateDTO | null | undefined,
  basis: Basis,
  scope: Scope,
  count: Count = "products",
): { money: number; units: number; figures: Figures } {
  const figures = figuresFor(aggregate, scope);
  return { money: money(figures, basis), units: units(figures, basis, count), figures };
}

/**
 * Billed money on invoices SIGM has not numbered yet.
 *
 * A subtraction of two named figures rather than a field of its own, because
 * "excluded" only means something under one scope and would have to be kept in
 * step with both. Zero when the aggregate carries no status split.
 */
export function notIssued(aggregate: InvoiceAggregateDTO | null | undefined): number {
  const sent = aggregate?.figures;
  return sent?.issued ? sent.all.billed - sent.issued.billed : 0;
}

/**
 * Billed, from a raw totals bucket.
 *
 * Part rows carry an `InvoiceTotals` and no figures object — there is no status
 * on them to split by — so a screen showing per-part or per-client cells needs
 * the same definition at that granularity. Same arithmetic as
 * `InvoiceFigures.billed`, spelled once.
 */
export function billedOf(totals: InvoiceTotals | null | undefined): number {
  return totals ? totals.line_total + totals.shipping : 0;
}

/** Sums figures across aggregates — currency totals, client rows, a period. */
export function total(parts: Figures[]): Figures {
  return parts.reduce<Figures>(
    (sum, part) => ({
      billed: sum.billed + part.billed,
      discounts: sum.discounts + part.discounts,
      returns: sum.returns + part.returns,
      net: sum.net + part.net,
      units_billed: sum.units_billed + part.units_billed,
      items_billed: sum.items_billed + part.items_billed,
    }),
    ZERO,
  );
}
